import { Badge, Button, Divider, Flex, Group, Menu, Select, Stack, Text, UnstyledButton } from '@mantine/core';
import { ArrowCounterClockwiseIcon, CaretDownIcon, FunnelSimpleIcon, XIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import type { FC } from 'react';

import { useCategoriesItemsQuery } from '../../../data/useCategoriesItemsQuery.ts';
import { useI18n } from '../../../hooks/useI18n.ts';

type SortOption = 'start' | '-start' | 'name' | '-name' | 'date_created' | '-date_created';
export type EventTypeFilter = 'online' | 'live' | 'secret' | undefined;

interface SortFilterMenuProps {
  sort: SortOption;
  categoryId: number | undefined;
  eventType: EventTypeFilter;
  /** Applied together, so the list refetches once instead of once per control. */
  onApply: (next: { sort: SortOption; categoryId: number | undefined; eventType: EventTypeFilter }) => void;
  onClearAll: () => void;
}

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'start', label: 'Date (nearest)' },
  { value: '-start', label: 'Date (furthest)' },
  { value: 'name', label: 'Name A–Z' },
  { value: '-name', label: 'Name Z–A' },
  { value: '-date_created', label: 'Newest' },
  { value: 'date_created', label: 'Oldest' },
];

const EVENT_TYPE_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'live', label: 'Live' },
  { value: 'secret', label: 'Secret' },
];

const selectStyles = {
  input: {
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 400,
    height: 48,
    minHeight: 48,
    padding: '12px 16px',
  },
  option: { borderRadius: 8, fontSize: 15, fontWeight: 400, padding: '8px 12px' },
  dropdown: { borderRadius: 12 },
};

// The select dropdowns must render inside the menu, not in a portal. Portalled, a click on an
// option counts as a click outside the menu and closes the whole panel, which made it impossible
// to set a second filter or reach Apply.
const comboboxProps = { dropdownPadding: 4, withinPortal: false };

export const SortFilterMenu: FC<SortFilterMenuProps> = ({ sort, categoryId, eventType, onApply, onClearAll }) => {
  const { t } = useI18n();
  const { data: categories } = useCategoriesItemsQuery();
  const [opened, setOpened] = useState(false);

  // The panel edits a draft. Nothing reaches the list until Apply, so half-set filters never
  // trigger a refetch and Cancel is a real escape rather than an undo.
  const [draft, setDraft] = useState({ categoryId, eventType, sort });

  useEffect(() => {
    if (opened) setDraft({ categoryId, eventType, sort });
  }, [opened, categoryId, eventType, sort]);

  const categoryOptions =
    categories?.map((cat) => ({
      value: String(cat.id),
      label: cat.title ?? `Category ${cat.id}`,
    })) ?? [];

  const activeFilterCount = (categoryId ? 1 : 0) + (eventType ? 1 : 0);

  const handleApply = () => {
    onApply(draft);
    setOpened(false);
  };

  const handleClearAll = () => {
    setDraft({ categoryId: undefined, eventType: undefined, sort: 'start' });
    onClearAll();
    setOpened(false);
  };

  return (
    <Menu
      closeOnItemClick={false}
      opened={opened}
      position="bottom-end"
      radius={16}
      shadow="lg"
      width={400}
      onChange={setOpened}
    >
      <Menu.Target>
        <UnstyledButton
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid var(--mantine-color-gigi-outlines-0)',
            borderRadius: 999,
            padding: '10px 18px',
          }}
        >
          <Group gap={8}>
            <FunnelSimpleIcon size={18} />
            <Text c="gray.7" fw={500} size="xxs">
              {t('dashboard_button--filters') ?? 'Filters'}
            </Text>
            {activeFilterCount > 0 && (
              <Badge color="grape.5" size="sm" variant="light">
                {activeFilterCount}
              </Badge>
            )}
            <CaretDownIcon
              size={16}
              style={{ transform: opened ? 'rotate(180deg)' : undefined, transition: 'transform 150ms ease' }}
            />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown p={0}>
        <Stack gap={0}>
          <Flex align="center" gap="xxs" justify="space-between" pb={12} pt={20} px={20}>
            <Text fw={700} style={{ fontSize: 20 }}>
              {t('dashboard_filter--title') ?? 'Filters & sorting'}
            </Text>

            <Group gap={12}>
              <UnstyledButton onClick={handleClearAll}>
                <Group gap={6}>
                  <ArrowCounterClockwiseIcon size={16} />
                  <Text c="gray.7" style={{ fontSize: 14 }}>
                    {t('dashboard_filter--clear-all') ?? 'Clear all'}
                  </Text>
                </Group>
              </UnstyledButton>

              <UnstyledButton aria-label="Close" onClick={() => setOpened(false)}>
                <XIcon size={20} />
              </UnstyledButton>
            </Group>
          </Flex>

          <Stack gap={4} px={20}>
            <Text c="gray.6" style={{ fontSize: 15 }}>
              {t('dashboard_filter--section-filter') ?? 'Filter'}
            </Text>

            <Text fw={500} mt={4} style={{ fontSize: 15 }}>
              {t('dashboard_filter--category') ?? 'Category'}
            </Text>
            <Select
              clearable
              comboboxProps={comboboxProps}
              data={categoryOptions}
              placeholder={t('dashboard_filter--all-categories') ?? 'All categories'}
              styles={selectStyles}
              value={draft.categoryId ? String(draft.categoryId) : null}
              onChange={(value) => setDraft((d) => ({ ...d, categoryId: value ? Number(value) : undefined }))}
            />

            <Text fw={500} mt={12} style={{ fontSize: 15 }}>
              {t('dashboard_filter--event-type') ?? 'Event type'}
            </Text>
            <Select
              clearable
              comboboxProps={comboboxProps}
              data={EVENT_TYPE_OPTIONS}
              placeholder={t('dashboard_filter--all-types') ?? 'All types'}
              styles={selectStyles}
              value={draft.eventType ?? null}
              onChange={(value) => setDraft((d) => ({ ...d, eventType: (value as EventTypeFilter) ?? undefined }))}
            />
          </Stack>

          <Divider my={20} />

          <Stack gap={4} px={20}>
            <Text c="gray.6" style={{ fontSize: 15 }}>
              {t('dashboard_filter--section-sort') ?? 'Sort'}
            </Text>

            <Text fw={500} mt={4} style={{ fontSize: 15 }}>
              {t('dashboard_sort--label') ?? 'Sort by'}
            </Text>
            <Select
              comboboxProps={comboboxProps}
              data={SORT_OPTIONS}
              styles={selectStyles}
              value={draft.sort}
              onChange={(value) => value && setDraft((d) => ({ ...d, sort: value }))}
            />
          </Stack>

          <Group gap={12} justify="flex-end" pb={20} pt={24} px={20}>
            <Button color="dark.9" radius="xl" size="md" variant="default" onClick={() => setOpened(false)}>
              <Text fw={500} style={{ fontSize: 15 }}>
                {t('dashboard_filter--cancel') ?? 'Cancel'}
              </Text>
            </Button>

            <Button color="dark.9" radius="xl" size="md" onClick={handleApply}>
              <Text c="white" fw={500} style={{ fontSize: 15 }}>
                {t('dashboard_filter--apply') ?? 'Apply'}
              </Text>
            </Button>
          </Group>
        </Stack>
      </Menu.Dropdown>
    </Menu>
  );
};
